import { cn } from "lib/utils";

const noop = () => {};

/**
 * Drop-in replacement for @material-ui/core's <Checkbox>.
 *
 * This is a styled native <input type="checkbox">, NOT the Radix-based
 * components/ui/checkbox — see the note at the bottom of this comment.
 *
 * Because it is a real input, the handler receives a real event: all 21 call
 * sites keep reading `e.target.checked` / `e.target.name` / `e.target.id`
 * exactly as they did under MUI, with no synthetic event and no handler
 * changes. MUI also passed the new value as a second argument, which is
 * mirrored here for the sites that might rely on it.
 *
 * MUI's root was a 42x42 round button (9px padding around a 24px icon) and it
 * received the caller's `className`/`style`, so those land on the padded
 * wrapper here — `style={{margin: "auto 0.5rem"}}` and friends keep meaning
 * what they used to. Appearance lives in the "Legacy control theming" block in
 * src/index.css, keyed on `.t4t-checkbox`, because the colour rules depend on
 * ancestor classes (`.card-disabled`, `.div-project-feature-listitem`).
 *
 * ---------------------------------------------------------------------------
 * WHY NATIVE AND NOT shadcn/Radix
 * ---------------------------------------------------------------------------
 * Originally native because Radix could not be bundled at all under CRA 5:
 * its ESM imports "react/jsx-runtime" extensionless, React 16 has no "exports"
 * map, and webpack 5 refused to resolve it. That blocker is GONE as of the Vite
 * migration — components/ui/checkbox (Radix) now builds fine, verified.
 *
 * Native is kept deliberately, because for a lone checkbox it is the better
 * primitive, not a workaround: it is accessible without JS, costs no bundle
 * weight, and hands handlers a real event, which is why all 21 call sites keep
 * reading e.target.checked / e.target.name with no adapter logic at all. Radix
 * earns its keep on composite widgets (Dialog, Select, DropdownMenu, Popover,
 * Tabs) where focus management and ARIA wiring are hard — not here.
 *
 * Revisit this when the shadcn design-token pass happens, not before: swapping
 * now would either reproduce the current MUI look on heavier primitives (pure
 * churn) or start the visual redesign one control ahead of the rest of the app.
 * ---------------------------------------------------------------------------
 */
export default function StandaloneCheckbox({
    checked,
    onChange,
    name,
    value,
    disabled,
    id,
    className,
    style,
}) {
    return (
        <span
            className={cn(
                "inline-flex items-center justify-center rounded-full p-[9px] align-middle",
                className
            )}
            style={style}
        >
            <input
                type="checkbox"
                className="t4t-checkbox"
                checked={!!checked}
                disabled={disabled}
                name={name}
                value={value}
                id={id}
                onChange={
                    onChange ? (e) => onChange(e, e.target.checked) : noop
                }
            />
        </span>
    );
}
